import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@starling/core/api/client';
import { ApiError } from '@starling/core/api/errors';
import { useAuthStore, type AuthUser, type TokenPair } from '@starling/core/auth/store';
import type { components } from '@/api/types.m1.gen';

// Хуки сессии Vision. СВОИ, не из core и не из Light (решение V1.2-a №4 /
// V1.3-b): формы не совпадают с Light 1:1 — у Light ContextRequired ведёт на
// экран «Войти как…», у Vision выбор контекста НЕ нужен (см. ниже), а
// phone-входа в Light нет вообще. Типы — из контракта openapi-m1.yaml
// (auth-эндпоинты платформенные, генерат — src/api/types.m1.gen.ts).

type LoginRequest = components['schemas']['LoginRequest'];
type PasswordResetRequest = components['schemas']['PasswordResetRequest'];
type PasswordResetConfirm = components['schemas']['PasswordResetConfirm'];
type EmailRequestCode = components['schemas']['EmailRequestCode'];
type EmailVerifyCode = components['schemas']['EmailVerifyCode'];
type PhoneRequestCode = components['schemas']['PhoneRequestCode'];
type PhoneVerifyCode = components['schemas']['PhoneVerifyCode'];
type GoogleLoginRequest = components['schemas']['GoogleLoginRequest'];
type ContextRequired = components['schemas']['ContextRequired'];

/** Ответ входа: пара токенов (0..1 контекст) либо запрос выбора (2+). */
export type LoginResponse = TokenPair | ContextRequired;

/** Дискриминатор union-ответа входа: мультиконтекстная форма. */
export function isContextRequired(data: LoginResponse): data is ContextRequired {
  return (data as ContextRequired).context_required === true;
}

/**
 * Приводит ответ входа к TokenPair (решение V1.3-b, «2+ оптики»).
 *
 * При 2+ активных membership бэкенд отдаёт `ContextRequired` (Light рисует
 * экран «Войти как…»). Vision'у tenant-контекст безразличен: все его
 * эндпоинты (`/organizations/*`, авторские `/vision/*`) читают только
 * `claims.sub`, а tenant-токен там валиден наравне с account-only. Поэтому
 * МОЛЧА обмениваем select_token на первый контекст через public
 * `POST /auth/select-context` — без экрана выбора (его нет в прототипе) и без
 * правок бэкенда. Полученная tenant-сессия для Vision функционально
 * эквивалентна account-only.
 */
async function resolveTokenPair(data: LoginResponse): Promise<TokenPair> {
  if (!isContextRequired(data)) return data;
  const first = data.contexts[0];
  if (!first) {
    // По контракту contexts непуст; страховка для noUncheckedIndexedAccess.
    throw new ApiError('context_selection_failed', 'Пустой список контекстов', 500);
  }
  return api.post<TokenPair>(
    '/api/v1/auth/select-context',
    { select_token: data.select_token, membership_id: first.membership_id },
    { skipAuth: true },
  );
}

/**
 * Общий каркас мутаций входа: обмен ввода на LoginResponse → TokenPair →
 * старт сессии. `qc.clear()` ДО setTokens — сброс React Query кэша прошлой
 * сессии (урок Light: ключи запросов не привязаны к аккаунту).
 */
function useSessionMutation<TInput>(
  mutationKey: readonly string[],
  exchange: (input: TInput) => Promise<LoginResponse>,
) {
  const setTokens = useAuthStore((s) => s.setTokens);
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...mutationKey],
    mutationFn: async (input: TInput) => resolveTokenPair(await exchange(input)),
    onSuccess: (pair) => {
      qc.clear();
      setTokens(pair);
    },
  });
}

/** Запрос SMS-кода входа (`POST /auth/phone/request-code`). ВСЕГДА 200 (анти-enumeration). */
export function usePhoneRequestCodeMutation() {
  return useMutation({
    mutationKey: ['auth', 'phone', 'request-code'],
    mutationFn: (input: PhoneRequestCode) =>
      api.post<{ status: string }>('/api/v1/auth/phone/request-code', input, { skipAuth: true }),
  });
}

/**
 * Вход/самозапись по коду из SMS (`POST /auth/phone/verify-code`).
 * `name` применяется бэкендом только при создании личности (V1.3-a);
 * для известного номера игнорируется. 400 `phone_otp_invalid` — единый код
 * (неверный/протух/попытки исчерпаны).
 */
export function usePhoneVerifyMutation() {
  return useSessionMutation(['auth', 'phone', 'verify-code'], (input: PhoneVerifyCode) =>
    api.post<LoginResponse>('/api/v1/auth/phone/verify-code', input, { skipAuth: true }),
  );
}

/** Запрос кода регистрации на email (`POST /auth/email/request-code`). ВСЕГДА 200 (анти-enumeration). */
export function useEmailSignupRequestCodeMutation() {
  return useMutation({
    mutationKey: ['auth', 'email', 'request-code'],
    mutationFn: (input: EmailRequestCode) =>
      api.post<{ status: string }>('/api/v1/auth/email/request-code', input, { skipAuth: true }),
  });
}

/**
 * Регистрация/вход по коду из письма (`POST /auth/email/verify-code`, доводка
 * V1). Email свободен → самозапись (password-identity: email + пароль, дальше
 * работает обычный /auth/login); занят активной личностью и пароль совпал →
 * обычный вход. 400 `email_taken` — занят иначе («войдите»); 400
 * `email_otp_invalid` — неверный/протухший код (единый, анти-enumeration).
 */
export function useEmailSignupVerifyMutation() {
  return useSessionMutation(['auth', 'email', 'verify-code'], (input: EmailVerifyCode) =>
    api.post<LoginResponse>('/api/v1/auth/email/verify-code', input, { skipAuth: true }),
  );
}

/** Вход по email/паролю (`POST /auth/login`). 401 — единый ответ (анти-enumeration). */
export function useEmailLoginMutation() {
  return useSessionMutation(['auth', 'login'], (input: LoginRequest) =>
    api.post<LoginResponse>('/api/v1/auth/login', input, { skipAuth: true }),
  );
}

/** Вход по Google id_token (`POST /auth/google`, GIS). 401 `google_auth_failed` — единый код. */
export function useGoogleLoginMutation() {
  return useSessionMutation(['auth', 'google'], (input: GoogleLoginRequest) =>
    api.post<LoginResponse>('/api/v1/auth/google', input, { skipAuth: true }),
  );
}

/**
 * Сброс пароля (доводка V1 №15): `product: 'vision'` — бренд/From письма и
 * база ссылки берутся из профиля Vision, ссылка ведёт на `/password-reset`
 * ЭТОГО приложения. Ответ всегда 200 (анти-enumeration) — любой не-200 это
 * сеть/инфраструктура.
 */
export function useRequestPasswordResetMutation() {
  return useMutation({
    mutationKey: ['auth', 'password-reset', 'request'],
    mutationFn: (input: { email: string }) =>
      api.post<{ status: string }>(
        '/api/v1/auth/password-reset/request',
        { email: input.email, product: 'vision' } satisfies PasswordResetRequest,
        { skipAuth: true },
      ),
  });
}

/** Новый пароль по reset-токену: 204; 400 `reset_token_invalid`; 422 — валидация. */
export function useConfirmPasswordResetMutation() {
  return useMutation({
    mutationKey: ['auth', 'password-reset', 'confirm'],
    mutationFn: (input: PasswordResetConfirm) =>
      api.post<void>('/api/v1/auth/password-reset/confirm', input, { skipAuth: true }),
  });
}

/** Выход: сброс кэша (гигиена данных аккаунта) + очистка токенов. */
export function useLogout(): () => void {
  const logout = useAuthStore((s) => s.logout);
  const qc = useQueryClient();
  return () => {
    qc.clear();
    logout();
  };
}

/** Состояние сессии для UI (шапка, гарды). */
export function useSession(): { isAuthenticated: boolean; user: AuthUser | null } {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const user = useAuthStore((s) => s.user);
  return { isAuthenticated, user };
}
